import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { InviteUserInput, Role, UpdateUserInput, User } from "@tunic/shared";
import { api } from "@/api/client";
import { qk } from "@/api/keys";
import { useMe } from "@/api/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, SelectField } from "@/components/projects/ProjectForm";
import { RelativeTime } from "@/components/common/RelativeTime";

export const Route = createFileRoute("/_app/settings/team")({ component: TeamPage });

const ROLE_LABEL: Record<Role, string> = { admin: "Admin", member: "Member" };

function TeamPage() {
  const qc = useQueryClient();
  const me = useMe();
  const users = useQuery({ queryKey: qk.users, queryFn: () => api.get<User[]>("/api/users") });
  const invite = useMutation({
    mutationFn: (input: InviteUserInput) => api.post<User>("/api/users", input),
    onSuccess: (u) => {
      qc.invalidateQueries({ queryKey: qk.users });
      qc.invalidateQueries({ queryKey: qk.team });
      toast.success(`Invited ${u.email}. They will get a sign-in link by email.`);
    },
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateUserInput }) => api.patch<User>(`/api/users/${id}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.users });
      qc.invalidateQueries({ queryKey: qk.team });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<InviteUserInput>({ email: "", name: "", role: "member" });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold">Team</h2>
          <p className="text-sm text-muted-foreground">Everyone sees every project. Admins manage people and tokens.</p>
        </div>
        <Button onClick={() => setOpen(true)}>Invite</Button>
      </div>
      <ul className="divide-y rounded-lg border bg-card">
        {users.data?.map((u) => (
          <li key={u.id} className="flex items-center gap-3 px-3 py-2 text-sm">
            <div className="min-w-0 flex-1">
              <div className="font-medium">
                {u.name} {!u.is_active && <span className="text-xs text-muted-foreground">(disabled)</span>}
              </div>
              <div className="text-xs text-muted-foreground">
                {u.email} · {u.last_login_at ? <>last seen <RelativeTime iso={u.last_login_at} /></> : "never signed in"}
              </div>
            </div>
            <div className="w-28">
              <SelectField value={u.role} onChange={(role) => update.mutate({ id: u.id, patch: { role } })} options={ROLE_LABEL} />
            </div>
            <Button
              variant="ghost"
              size="sm"
              disabled={u.id === me.data?.user.id}
              onClick={() => update.mutate({ id: u.id, patch: { is_active: !u.is_active } })}
            >
              {u.is_active ? "Disable" : "Enable"}
            </Button>
          </li>
        ))}
      </ul>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite a teammate</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="Name">
              <Input autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="Email">
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label="Role">
              <SelectField value={form.role} onChange={(role) => setForm({ ...form, role })} options={ROLE_LABEL} />
            </Field>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                disabled={!form.email || !form.name || invite.isPending}
                onClick={async () => {
                  await invite.mutateAsync(form);
                  setOpen(false);
                  setForm({ email: "", name: "", role: "member" });
                }}
              >
                Send invite
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
